import { useEffect, useState } from 'react'
import {
  Card, Col, Empty, List, Row, Space, Statistic, Tag, Typography,
} from 'antd'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import { maintenanceDashboardApi, type ReportFilters } from '@/features/maintenance/api'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ReportFilterBar from './components/ReportFilterBar'
import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'
import { useMasterLookups } from './components/useMasterLookups'

const { Text } = Typography

interface WOSummary {
  id: string
  work_order_no: string
  machine_id: string | null
  priority: string
  status: string
}

/**
 * Spec §2 — Maintenance Manager Dashboard.
 * All numbers come from the backend manager_dashboard_summary endpoint so
 * the screen is mathematically consistent with the underlying reports.
 * Drill-down: every list item links to the Work Order Board.
 */
export default function MaintenanceManagerDashboardPage() {
  const { plantId } = usePlantId()
  const { machineCode, deptName } = useMasterLookups()
  const [filters, setFilters] = useState<ReportFilters>({
    plant_id: plantId, from_date: dayjs().format('YYYY-MM-DD'),
    to_date: dayjs().format('YYYY-MM-DD'),
  })
  // Keep filters in sync with the active plant (selector lives in header).
  useEffect(() => {
    if (filters.plant_id !== plantId) setFilters((f) => ({ ...f, plant_id: plantId }))
  }, [plantId, filters.plant_id])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['mgr-dashboard', filters],
    enabled: !!filters.plant_id,
    queryFn: () => maintenanceDashboardApi.manager(filters),
  })

  const n = (k: string) => Number((data as Record<string, unknown>)?.[k] ?? 0)
  const arr = <T = WOSummary,>(k: string) =>
    ((data as Record<string, unknown>)?.[k] as unknown as T[]) ?? []
  const dist = (k: string) =>
    ((data as Record<string, unknown>)?.[k] as Record<string, number>) ?? {}

  return (
    <div>
      <PageHeader
        title="Maintenance Manager Dashboard"
        subtitle="Today's operating position — every number is backend-calculated, drill down to act."
      />

      <ReportFilterBar
        filters={filters}
        onChange={setFilters}
        onRefresh={() => refetch()}
        show={{ dateRange: true }}
      />

      {isLoading ? (
        <Card><Text type="secondary">Loading dashboard…</Text></Card>
      ) : !data ? (
        <Empty />
      ) : (
        <>
          {/* Row 1 — operational pulse */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={12} md={6} xl={4}>
              <Card><Statistic title="Open WOs" value={n('open_work_orders')} /></Card>
            </Col>
            <Col xs={12} md={6} xl={4}>
              <Card>
                <Statistic
                  title="Critical WOs"
                  value={n('critical_work_orders')}
                  valueStyle={{ color: n('critical_work_orders') > 0 ? '#cf1322' : undefined }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6} xl={4}>
              <Card>
                <Statistic
                  title="Machines in breakdown"
                  value={n('machines_under_breakdown')}
                />
              </Card>
            </Col>
            <Col xs={12} md={6} xl={4}>
              <Card><Statistic title="Waiting for spares" value={n('waiting_for_spares')} valueStyle={{ color: '#faad14' }} /></Card>
            </Col>
            <Col xs={12} md={6} xl={4}>
              <Card>
                <Statistic
                  title="Safety risk open"
                  value={n('safety_risk_open_jobs')}
                  valueStyle={{ color: n('safety_risk_open_jobs') > 0 ? '#cf1322' : undefined }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6} xl={4}>
              <Card><Statistic title="Critical spare low stock" value={n('critical_spare_low_stock')} valueStyle={{ color: n('critical_spare_low_stock') > 0 ? '#fa541c' : undefined }} /></Card>
            </Col>
          </Row>

          {/* Row 2 — PM + downtime + workload */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={12} md={6}><Card><Statistic title="PM due today" value={n('pm_due_today')} /></Card></Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="PM overdue"
                  value={n('pm_overdue')}
                  valueStyle={{ color: n('pm_overdue') > 0 ? '#cf1322' : undefined }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}><Card><Statistic title="PM completed today" value={n('pm_completed_today')} valueStyle={{ color: '#52c41a' }} /></Card></Col>
            <Col xs={12} md={6}><Card><Statistic title="Tech jobs pending" value={n('technician_jobs_pending')} /></Card></Col>
          </Row>

          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={24} md={12}>
              <Card><Statistic title="Total downtime today (min)" value={n('total_downtime_today_minutes')} /></Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Top downtime machine today" size="small">
                {(() => {
                  const t = (data as Record<string, unknown>)?.top_downtime_machine_today as
                    { machine_id: string | null; downtime_minutes: number } | undefined
                  if (!t || !t.machine_id) return <Text type="secondary">No downtime recorded yet.</Text>
                  return (
                    <Space>
                      <Text strong>{machineCode(t.machine_id)}</Text>
                      <Text>— {t.downtime_minutes} min</Text>
                    </Space>
                  )
                })()}
              </Card>
            </Col>
          </Row>

          {/* Row 3 — distributions */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={24} md={8}>
              <Card title="Status distribution" size="small">
                {Object.keys(dist('status_distribution')).length === 0
                  ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  : <List
                      size="small"
                      dataSource={Object.entries(dist('status_distribution'))}
                      renderItem={([k, v]) => (
                        <List.Item>
                          <Space><MaintenanceStatusBadge status={k} /></Space>
                          <Text strong>{v}</Text>
                        </List.Item>
                      )}
                    />}
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card title="Priority distribution" size="small">
                {Object.keys(dist('priority_distribution')).length === 0
                  ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  : <List
                      size="small"
                      dataSource={Object.entries(dist('priority_distribution'))}
                      renderItem={([k, v]) => (
                        <List.Item>
                          <MaintenanceStatusBadge status={k} />
                          <Text strong>{v}</Text>
                        </List.Item>
                      )}
                    />}
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card title="Downtime by department (min)" size="small">
                {Object.keys(dist('department_downtime_minutes')).length === 0
                  ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  : <List
                      size="small"
                      dataSource={Object.entries(dist('department_downtime_minutes'))}
                      renderItem={([k, v]) => (
                        <List.Item>
                          <Text>{deptName(k)}</Text>
                          <Text strong>{v} min</Text>
                        </List.Item>
                      )}
                    />}
              </Card>
            </Col>
          </Row>

          {/* Row 4 — actionable lists */}
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12}>
              <Card title="Critical / open WOs" size="small" extra={<Link to="/maintenance/work-orders">Board →</Link>}>
                {arr<WOSummary>('critical_open_list').length === 0
                  ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No critical jobs open" />
                  : <List
                      size="small"
                      dataSource={arr<WOSummary>('critical_open_list')}
                      renderItem={(wo) => (
                        <List.Item>
                          <Space size={6} wrap>
                            <Text strong>{wo.work_order_no}</Text>
                            <Text type="secondary">{machineCode(wo.machine_id)}</Text>
                            <MaintenanceStatusBadge status={wo.priority} />
                            <MaintenanceStatusBadge status={wo.status} />
                          </Space>
                        </List.Item>
                      )}
                    />}
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Waiting for spares" size="small" extra={<Link to="/maintenance/spares">Spare desk →</Link>}>
                {arr<WOSummary>('waiting_for_spares_list').length === 0
                  ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No WOs waiting for spares" />
                  : <List
                      size="small"
                      dataSource={arr<WOSummary>('waiting_for_spares_list')}
                      renderItem={(wo) => (
                        <List.Item>
                          <Space size={6} wrap>
                            <Text strong>{wo.work_order_no}</Text>
                            <Text type="secondary">{machineCode(wo.machine_id)}</Text>
                            <Tag color="gold">WAITING SPARE</Tag>
                          </Space>
                        </List.Item>
                      )}
                    />}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  )
}
